import React, { useEffect, useMemo, useRef, useState } from 'react';
import AsyncSelect from 'react-select/async';
import Layout from '../Layout';
import ModalManager, { ModalCallbacks } from './ModalManager';
import PreviewPDF from './PreviewPDF';
import InsuranceAddonModal from './InsuranceAddonModal';
import VisaDetailsModal from '../PackageBuilder/modals/VisaDetailsModal';
import CustomFlightModal from '../PackageBuilder/modals/CustomFlightModal';
import ShareQuotationButton from '../PackageBuilder/ShareQuotationButton';
import RoomsTravellersDropdown from '../Common/RoomsTravellersDropdown';
import StandaloneQuotationWorkflow from './StandaloneQuotationWorkflow';
import { useLocation, useNavigate } from 'react-router-dom';
import { useRole } from '../../hooks/usePermissions';
import usePageExitConfirmation from '../../hooks/usePageExitConfirmation';
import { extractLatestBookingDraft } from '../../api/BookingDraft';
import { extractQuotationItinerary, ExtractionWarning, QuotationItineraryBuilderSeed } from '../../api/QuotationItineraryAI';
import { usePackageBuilderState } from '../../hooks/usePackageBuilderState';
import { usePackageSave } from '../../hooks/usePackageSave';
import { fetchPackagesPage } from '../../api/Packages';
import { loadAirportLocations } from '../../api/Location';
import { TravelPackage } from '../../types/TravelPackage';
import { Flight, Hotel, ItineraryItem } from '../../types/Quotation';
import { Activity } from '../../types/Activity';
import { estimateActivities, fetchActivities, type ActivityEstimate, type ActivitySummary } from '../../api/Activities';
import { LocationOption } from '../../types/Location';
import { InsuranceDetail, VisaDetail } from '../../types/PackageBuilder';
import { formatTime, getDatePlusDays } from '../../utils/date';
import { getHotelMealType } from '../../utils/hotelMealType';
import {
  buildDefaultQuotationRooms,
  normalizeQuotationRooms,
  QuotationRoomOccupancy,
  summarizeQuotationRooms,
  toTravellerInfoFromRooms,
} from './quotationRoomOccupancy';
import { BookingHotelSummary, buildBookingComponentsSummary, formatDisplayDateTime } from '../../utils/bookingComponents';
import {
  isPackageBuilderLocationState,
  PackageBuilderLocationState,
} from '../PackageBuilder/contracts';
import {
  descriptionLimits,
  faqLimits,
  highlightLimits,
} from '../PackageBuilder/editors/sharedSectionProps';
import '../../css/QuotationBuilder.css';
import '../../css/PreviewPDF.css';
import '../../css/modal.css';
import '../../css/package-builder/sections.css';
import '../../css/package-builder/preview.css';
import '../../css/package-builder/guided.css';

const DEFAULT_QUOTATION_MARKUP_PERCENT = 15;
const MIN_QUOTATION_MARKUP_PERCENT = 12;
const QUOTATION_GST_RATE = 0.05;
const FOUR_HOURS_MS = 4 * 60 * 60 * 1000;

export const formatAirportOptionLabel = (option: LocationOption): string => {
  const city = option.label.trim();
  const airportCode = option.value.trim();

  if (!city || !airportCode || city.toLowerCase() === airportCode.toLowerCase()) {
    return city || airportCode;
  }

  return `${city} - ${airportCode}`;
};

type WorkflowCardProps = {
  step: number;
  title: string;
  subtitle: string;
  children: React.ReactNode;
};

const WorkflowCard: React.FC<WorkflowCardProps> = ({ step, title, subtitle, children }) => (
  <section className="quotation-workflow-card">
    <div className="quotation-workflow-card__header">
      <div className="quotation-workflow-card__step">{step}</div>
      <div>
        <h2>{title}</h2>
        <p>{subtitle}</p>
      </div>
    </div>
    {children}
  </section>
);

const getPackageImage = (pkg: TravelPackage) => (
  pkg.hero_image
  || pkg.gallery?.[0]
  || pkg.hotels?.[0]?.image
  || 'https://cdn.otaprime.com/logo-new.png'
);

const flattenFlights = (itinerary: ItineraryItem[]) =>
  itinerary.flatMap((day) => day.flights || []);

const flattenHotels = (itinerary: ItineraryItem[]) => {
  const displayedHotelKeys = new Set<string>();

  return itinerary.flatMap((day, dayIndex) => (day.hotels || []).flatMap((hotel) => {
    // A selected hotel is stored on each itinerary day that belongs to its stay.
    // Show that stay once in the quotation summary, while retaining the per-day
    // itinerary entries used by the rest of the builder.
    const hotelKey = [
      hotel.hotelId || hotel.hotelRef || hotel.hotelCode || hotel.name,
      hotel.checkInDate || '',
      hotel.checkOutDate || '',
    ].join('|');
    if (displayedHotelKeys.has(hotelKey)) return [];
    displayedHotelKeys.add(hotelKey);
    return [{ ...hotel, dayIndex }];
  }));
};

const flattenActivities = (itinerary: ItineraryItem[]) =>
  itinerary.flatMap((day, dayIndex) =>
    (day.activities || []).filter((activity) => activity.inclusionType !== 'optional').map((activity, activityIndex) => ({
      activity,
      activityIndex,
      dayIndex,
      day: day.day,
    }))
  );

const normalizeHotelIdentifier = (value: unknown) => String(value || '').trim().toLowerCase();

const getHotelIdentifiers = (hotel: Partial<Hotel>) => new Set([
  hotel.hotelId,
  hotel.hotelCode,
  hotel.hotelRef,
  hotel.hotel_ref,
  hotel.providerHotelId,
].map(normalizeHotelIdentifier).filter(Boolean));

const getBookingHotelIdentifiers = (hotel: BookingHotelSummary) => new Set([
  hotel.componentId,
  hotel.hotelId,
  hotel.hotelCode,
  hotel.hotelRef,
].map(normalizeHotelIdentifier).filter(Boolean));

const findMatchingBookingHotel = (hotel: Hotel, snapshots: BookingHotelSummary[]) => {
  const hotelIdentifiers = getHotelIdentifiers(hotel);
  if (hotelIdentifiers.size === 0) return undefined;

  return snapshots.find((snapshot) =>
    Array.from(getBookingHotelIdentifiers(snapshot)).some((identifier) => hotelIdentifiers.has(identifier))
  );
};

export const mergeBookingHotelSnapshots = (
  itinerary: ItineraryItem[],
  snapshots: BookingHotelSummary[]
): ItineraryItem[] => itinerary.map((day) => ({
  ...day,
  hotels: (day.hotels || []).map((hotel) => {
    const snapshot = findMatchingBookingHotel(hotel, snapshots);
    if (!snapshot) return hotel;

    return {
      ...hotel,
      provider: snapshot.provider || hotel.provider,
      hotelId: snapshot.hotelId || hotel.hotelId,
      hotelCode: snapshot.hotelCode || hotel.hotelCode,
      hotelRef: snapshot.hotelRef || hotel.hotelRef,
      name: snapshot.name,
      starRating: snapshot.starRating || '',
      address: snapshot.address || '',
      description: snapshot.description || '',
      city_name: snapshot.cityName || hotel.city_name,
      city_code: snapshot.cityCode || hotel.city_code,
      checkin_time: snapshot.checkInTime || hotel.checkin_time,
      checkout_time: snapshot.checkOutTime || hotel.checkout_time,
      latitude: snapshot.latitude ?? hotel.latitude,
      longitude: snapshot.longitude ?? hotel.longitude,
      phone_number: snapshot.phoneNumber || hotel.phone_number,
      price: snapshot.price ?? hotel.price,
      currency: snapshot.currency || hotel.currency,
      display_price: snapshot.displayPrice ?? hotel.display_price,
      display_currency: snapshot.displayCurrency || hotel.display_currency,
      amenities: snapshot.amenities || [],
      thumbnail: snapshot.thumbnail || null,
      images: snapshot.images || [],
      gallery: snapshot.gallery || [],
      roomType: snapshot.room,
      roomVariantId: snapshot.roomVariantId || hotel.roomVariantId,
      roomVariant: snapshot.roomVariant ?? hotel.roomVariant,
      checkInDate: snapshot.checkInDate,
      checkOutDate: snapshot.checkOutDate,
    } as Hotel;
  }),
}));

const formatCabType = (value: string) => value.replace(/\s+/g, ' ').trim();

const buildDisplayedCabRows = (rows: Array<{
  id?: string;
  tempId?: string;
  cabType: string;
  maxOccupancy: number;
  price: number;
  isActive: boolean;
  isDeleted?: boolean;
}>) => {
  return rows.filter((row) => Number(row.price || 0) > 0);
};

const formatMoney = (amount: number, currency: 'INR' | 'USD' = 'INR') => {
  const symbol = currency === 'USD' ? '$' : '₹';
  return `${symbol} ${Number(amount || 0).toLocaleString('en-IN')}`;
};

const cleanActivityDescription = (value?: string | null) => String(value || '')
  .replace(/<[^>]*>/g, ' ')
  .replace(/\s+/g, ' ')
  .trim();

const getCatalogTicketTotal = (
  activity: ActivitySummary,
  travellers: { adults: number; children: number }
): number | null => {
  const adultPrice = Number(activity.ticketPrices?.adultPrice);
  const childPrice = Number(activity.ticketPrices?.childPrice);
  if (!Number.isFinite(adultPrice) && !Number.isFinite(childPrice)) return null;

  const total = Math.max(0, Number.isFinite(adultPrice) ? adultPrice : 0) * travellers.adults
    + Math.max(0, Number.isFinite(childPrice) ? childPrice : 0) * travellers.children;
  return total > 0 ? total : null;
};

const getTicketOnlyTotal = (
  activity: ActivitySummary,
  estimate: ActivityEstimate | undefined,
  travellers: { adults: number; children: number }
): number | null => {
  const estimatedTicketTotal = Number(estimate?.ticketTotal);
  if (Number.isFinite(estimatedTicketTotal) && estimatedTicketTotal > 0) {
    return estimatedTicketTotal;
  }
  return getCatalogTicketTotal(activity, travellers);
};

export const formatDuration = (pkg?: TravelPackage) => {
  if (!pkg) return '';
  const stopNights = (pkg.stops || []).reduce((total, stop) => total + Math.max(0, Number(stop.nights || 0)), 0);
  const nights = stopNights || Number(pkg.durationNights ?? pkg.duration_nights ?? 0);
  const days = nights > 0 ? nights + 1 : Number(pkg.durationDays ?? pkg.duration_days ?? 0);
  return `${nights} Nights / ${days} Days`;
};

export const applyQuotationGst = (amount: number) => Math.round(Math.max(0, Number(amount || 0)) * (1 + QUOTATION_GST_RATE));

const isOlderThanFourHours = (value?: string) => {
  if (!value) return false;
  const timestamp = new Date(value).getTime();
  if (!Number.isFinite(timestamp)) return false;
  return Date.now() - timestamp > FOUR_HOURS_MS;
};

const QuotationWorkflowPage: React.FC = () => {
  const { confirmation: exitConfirmation } = usePageExitConfirmation('Your in-progress quotation will be lost.');
  const location = useLocation();
  const navigate = useNavigate();
  const locationState: PackageBuilderLocationState | undefined = isPackageBuilderLocationState(location.state)
    ? location.state
    : undefined;

  const role = useRole();
  const step = locationState?.quotationStep === 'review' ? 'review' : 'builder';
  const quotationKind = locationState?.quotationKind === 'flight' || locationState?.quotationKind === 'hotel'
    ? locationState.quotationKind
    : 'package';
  const packageToCopy = locationState?.packageToCopy;
  const sourceQueryId = typeof locationState?.queryId === 'number' ? locationState.queryId : undefined;
  const [lastSharedQuotationAt, setLastSharedQuotationAt] = useState<string | undefined>(locationState?.lastSharedQuotationAt);
  const [hasOpenedStandaloneQuotation, setHasOpenedStandaloneQuotation] = useState(quotationKind !== 'package');
  const [latestBookingDraft, setLatestBookingDraft] = useState<any | null>(null);
  const [isShareConfirmationOpen, setIsShareConfirmationOpen] = useState(false);

  const [packageSearch, setPackageSearch] = useState('');
  const [packages, setPackages] = useState<TravelPackage[]>([]);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [packagePage, setPackagePage] = useState(1);
  const [, setPackageTotalPages] = useState(1);
  const [hotelRooms, setHotelRooms] = useState<QuotationRoomOccupancy[]>(() => normalizeQuotationRooms(locationState?.hotelRooms || buildDefaultQuotationRooms()));
  const [rawItineraryText, setRawItineraryText] = useState('');
  const [isExtractingItinerary, setIsExtractingItinerary] = useState(false);
  const [itineraryExtractionError, setItineraryExtractionError] = useState('');
  const [itineraryExtractionWarnings, setItineraryExtractionWarnings] = useState<ExtractionWarning[]>([]);
  const [isVendorItineraryOpen, setIsVendorItineraryOpen] = useState(false);
  const [pendingBuilderSeed, setPendingBuilderSeed] = useState<QuotationItineraryBuilderSeed | null>(null);
  const [activityPickerDayIndex, setActivityPickerDayIndex] = useState<number | null>(null);
  const [activitySearch, setActivitySearch] = useState('');
  const [activityCatalogCity, setActivityCatalogCity] = useState('');
  const [activityCatalog, setActivityCatalog] = useState<ActivitySummary[]>([]);
  const [activityCatalogPrices, setActivityCatalogPrices] = useState<Record<string, ActivityEstimate>>({});
  const [isLoadingActivityCatalog, setIsLoadingActivityCatalog] = useState(false);
  const [activityCatalogError, setActivityCatalogError] = useState('');

  useEffect(() => {
    if (!sourceQueryId) return;

    let active = true;
    void (async () => {
      const latestDraft = await extractLatestBookingDraft(sourceQueryId);
      if (!active || !latestDraft) return;
      setLatestBookingDraft(latestDraft);
      const draftTimestamp = latestDraft.updated_at || latestDraft.updatedAt || latestDraft.created_at || latestDraft.createdAt;
      if (!lastSharedQuotationAt && draftTimestamp) {
        setLastSharedQuotationAt(String(draftTimestamp));
      }
    })();

    return () => {
      active = false;
    };
  }, [lastSharedQuotationAt, sourceQueryId]);

  const builderState = usePackageBuilderState({
    builderMode: 'quotation',
    sourceQueryId,
    packageToCopy,
    hotelRooms,
    highlightLimits,
    faqLimits,
  });

  const { coreState, pricingState, contentState, modalState, assetsState, derivedState, actions } = builderState;
  const quotationPdfRef = useRef<HTMLDivElement | null>(null);
  const quotationTotalPrice = useMemo(
    () => applyQuotationGst(derivedState.displayedTotal),
    [derivedState.displayedTotal]
  );
  const resolvePlaceIndexForDay = (dayIndex: number) => {
    const placeBoundaries = coreState.tripInfo.places.reduce<{ start: number; end: number; idx: number }[]>((acc, place, idx) => {
      const start = acc.length === 0 ? 0 : acc[acc.length - 1].end;
      const end = start + Math.max(0, Number(place.nights || 0));
      acc.push({ start, end, idx });
      return acc;
    }, []);
    const hit = placeBoundaries.find((boundary) => dayIndex >= boundary.start && dayIndex < boundary.end);
    if (hit) return hit.idx;
    return Math.max(0, coreState.tripInfo.places.length - 1);
  };

  useEffect(() => {
    if (pricingState.markupType !== 'percent') {
      actions.setMarkupType('percent');
      return;
    }

    const currentMarkup = Number(pricingState.markupValue || 0);
    if (currentMarkup === 0) {
      actions.setMarkupValue(DEFAULT_QUOTATION_MARKUP_PERCENT);
      return;
    }

    if (currentMarkup < MIN_QUOTATION_MARKUP_PERCENT) {
      actions.setMarkupValue(MIN_QUOTATION_MARKUP_PERCENT);
    }
  }, [actions, pricingState.markupType, pricingState.markupValue]);

  useEffect(() => {
    if (!pendingBuilderSeed) return;
    const expectedNights = pendingBuilderSeed.tripInfo.places.reduce((sum, place) => sum + Number(place.nights || 0), 0);
    if (coreState.nightsCount !== expectedNights) return;

    actions.setItinerary(pendingBuilderSeed.itinerary as ItineraryItem[]);
    setPendingBuilderSeed(null);
  }, [actions, coreState.nightsCount, pendingBuilderSeed]);

  const bookingPlanData = latestBookingDraft?.booking_plan || latestBookingDraft?.bookingPlan || null;
  const derivedEndDate = coreState.tripInfo.startDate ? getDatePlusDays(coreState.tripInfo.startDate, coreState.nightsCount) : '';
  const latestBookingPlanSummary = useMemo(() => buildBookingComponentsSummary({
    bookingPlan: bookingPlanData,
    travelStartDate: coreState.tripInfo.startDate,
    travelEndDate: derivedEndDate,
    totalAmount: quotationTotalPrice,
  }), [
    bookingPlanData,
    coreState.tripInfo.startDate,
    derivedEndDate,
    quotationTotalPrice,
  ]);

  const displayItinerary = useMemo<ItineraryItem[]>(() => {
    if (!latestBookingPlanSummary) return coreState.itinerary;

    const flightSnapshots = latestBookingPlanSummary.flights;
    const itineraryWithDraftHotels = mergeBookingHotelSnapshots(
      coreState.itinerary,
      latestBookingPlanSummary.hotels
    );
    let flightIndex = 0;

    return itineraryWithDraftHotels.map((day) => {
      const flights = (day.flights || []).map((flight) => {
        const snapshot = flightSnapshots[flightIndex];
        flightIndex += 1;
        if (!snapshot) return flight;

        return {
          ...flight,
          from: snapshot.from,
          to: snapshot.to,
          airline: snapshot.airline,
          flightNumber: snapshot.flightNumber,
          departureTime: snapshot.departureTime,
          arrivalTime: snapshot.arrivalTime,
          duration: snapshot.duration || flight.duration,
          stops: snapshot.stopsCount ?? flight.stops,
          provider: snapshot.provider || flight.provider,
          price: snapshot.price ?? flight.price,
        } as Flight;
      });

      return {
        ...day,
        flights,
      } as ItineraryItem;
    });
  }, [coreState.itinerary, latestBookingPlanSummary]);

  const { buildPackagePayload } = usePackageSave({
    trip: {
      builderMode: 'quotation',
      tripInfo: coreState.tripInfo,
      itinerary: coreState.itinerary,
      nightsCount: coreState.nightsCount,
    },
    pricing: {
      finalTotal: derivedState.finalTotal,
      strikePrice: pricingState.strikePrice,
      pricePer: pricingState.pricePer,
      currency: pricingState.currency,
      hotelCategory: contentState.hotelCategory,
      rating: pricingState.rating,
      mealPlan: contentState.mealPlan,
      transferType: contentState.transferType,
      transferPricing: pricingState.transferPricing,
      flightsTotal: derivedState.flightsTotal,
      hotelsTotal: derivedState.hotelsTotal,
      activitiesTotal: derivedState.activitiesTotal,
      transfersTotal: derivedState.transfersTotal,
      addonsTotalForPackage: derivedState.addonsTotalForPackage,
      visaTotal: derivedState.visaTotal,
      insuranceTotal: derivedState.insuranceTotal,
      inclusionsTotal: derivedState.inclusionsTotal,
      markupType: pricingState.markupType,
      markupValue: pricingState.markupValue,
      markupAmount: derivedState.markupAmount,
    },
    content: {
      highlights: contentState.highlights,
      highlightLimits,
      descriptionLimits,
      faqLimits,
      idealFor: contentState.idealFor,
      idealVendor: contentState.idealVendor,
      searchTags: contentState.searchTags,
      relatedDestinations: contentState.relatedDestinations,
      relatedPackages: contentState.relatedPackages,
      exclusions: contentState.exclusions,
      addons: contentState.addons,
      reviews: contentState.reviews,
      faq: contentState.faq,
      isPublished: pricingState.isPublished,
      visaDetails: contentState.visaDetails,
      insuranceDetails: contentState.insuranceDetails,
      customVisaDetails: contentState.customVisaDetails,
      customInsuranceDetails: contentState.customInsuranceDetails,
      customFlights: contentState.customFlights,
      customActivityInclusions: contentState.customActivityInclusions,
    },
    media: {
      heroImageFile: assetsState.heroImageFile,
      galleryFiles: assetsState.galleryFiles,
      hotelImageFiles: assetsState.hotelImageFiles,
    },
    meta: {
      editingId: coreState.editingId,
      slug: derivedState.slug,
    },
  });

  useEffect(() => {
    if (!packageSearch.trim()) {
      setLoadingPackages(false);
      setPackages([]);
      setPackageTotalPages(1);
      return;
    }

    let active = true;
    const timer = window.setTimeout(async () => {
      try {
        setLoadingPackages(true);
        const response = await fetchPackagesPage({
          search: packageSearch.trim() || undefined,
          page: packagePage,
          pageSize: 6,
        });
        if (!active) return;
        setPackages(response.items || []);
        setPackageTotalPages(response.pagination.totalPages || 1);
      } catch {
        if (!active) return;
        setPackages([]);
        setPackageTotalPages(1);
      } finally {
        if (active) setLoadingPackages(false);
      }
    }, 250);

    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [packagePage, packageSearch]);

  const selectedPackage = packageToCopy;
  const visiblePackages = useMemo(() => {
    const combinedPackages = selectedPackage
      ? [selectedPackage, ...packages]
      : packages;

    return combinedPackages.filter((pkg, index, allPackages) => {
      const pkgKey = pkg.id || pkg.slug;
      return index === allPackages.findIndex((candidate) => (candidate.id || candidate.slug) === pkgKey);
    });
  }, [packages, selectedPackage]);
  const flightList = useMemo(() => flattenFlights(displayItinerary), [displayItinerary]);
  const hotelList = useMemo(() => flattenHotels(displayItinerary), [displayItinerary]);
  const activityList = useMemo(() => flattenActivities(coreState.itinerary), [coreState.itinerary]);
  const selectedPackageTravel = selectedPackage as Partial<TravelPackage> | undefined;
  const arrivalCityLabel = (
    selectedPackageTravel?.arrivalAt
    || selectedPackageTravel?.arrival_at
    || ''
  ).trim();
  const departureCityLabel = (
    selectedPackageTravel?.departureFrom
    || selectedPackageTravel?.departure_from
    || ''
  ).trim();
  const arrivalPlace = arrivalCityLabel || coreState.tripInfo.places[0]?.label || '';
  const departurePlace = departureCityLabel || coreState.tripInfo.places[coreState.tripInfo.places.length - 1]?.label || arrivalPlace;
  const arrivalFlightCount = coreState.itinerary[0]?.flights?.length || 0;
  const departureFlightCount = coreState.itinerary[coreState.nightsCount]?.flights?.length || 0;
  const hasDistinctArrivalAndDepartureCities = Boolean(
    arrivalPlace
    && departurePlace
    && arrivalPlace !== departurePlace
  );
  // Room occupancy is the editable quotation source of truth. The query may still
  // contain its old pax while a quotation is being revised.
  const roomTravellers = useMemo(() => toTravellerInfoFromRooms(hotelRooms), [hotelRooms]);
  const totalPassengers = Number(roomTravellers.adults || 0) + Number(roomTravellers.children || 0);
  const hotelRoomSummary = useMemo(() => summarizeQuotationRooms(hotelRooms), [hotelRooms]);
  const hotelMealType = getHotelMealType(contentState.mealPlan);
  const packageCabOptions = builderState.quotationPackageState.transferRows;
  const draftTransferSelection = (locationState?.packageToCopy as any)?.transferCalculation
    || null;
  const selectedCabRowId = builderState.derivedState.quotationTransferCalculation?.sourceRowId
    || draftTransferSelection?.sourceRowId;
  const selectedCabLabel = builderState.derivedState.quotationTransferCalculation?.cabType
    || draftTransferSelection?.cabType;
  const displayedCabOptions = useMemo(() => buildDisplayedCabRows(packageCabOptions), [packageCabOptions]);
  const quotationTravellers = useMemo(() => ({
    adults: Math.max(1, Number(roomTravellers.adults || 0)),
    children: Math.max(0, Number(roomTravellers.children || 0)),
    infants: Math.max(0, Number(coreState.tripInfo.travellers?.infants || 0)),
  }), [coreState.tripInfo.travellers?.infants, roomTravellers]);
  const activitiesToPrice = useMemo(() => coreState.itinerary
    .flatMap((day) => (day.activities || []).map((activity) => ({
      activity,
      activityRef: activity.activity_ref || activity.activityRef,
    })))
    .filter(({ activity, activityRef }) => activity.inclusionType !== 'optional' && Boolean(activityRef)), [coreState.itinerary]);

  const quoteActivity = async (activityRef: string, cabType?: string) => {
    const result = await estimateActivities({
      activityRefs: [{ activityRef, ...(cabType ? { cabType } : {}) }],
      travelDate: coreState.tripInfo.startDate || derivedState.defaultHotelStartDate,
      ...quotationTravellers,
    });
    return result.items[activityRef];
  };

  const toQuotedActivity = (activity: ActivitySummary, estimate?: ActivityEstimate): Activity => {
    const hasLiveEstimate = estimate?.status === 'AVAILABLE';
    const ticketOnlyTotal = getTicketOnlyTotal(activity, estimate, quotationTravellers);
    const ticketTotal = hasLiveEstimate
      ? Math.max(0, Number(estimate?.ticketTotal || 0))
      : Math.max(0, Number(ticketOnlyTotal || 0));
    const cabTotal = hasLiveEstimate ? Math.max(0, Number(estimate?.transferTotal || 0)) : 0;
    const quotedTotal = hasLiveEstimate
      ? Math.max(0, Number(estimate?.totalAmount ?? ticketTotal + cabTotal))
      : ticketTotal;

    return {
      id: activity.id,
      slug: activity.id,
      activity_ref: activity.id,
      activityRef: activity.id,
      provider: activity.provider,
      name: activity.name,
      description: activity.description || '',
      picture_url: activity.image_path || undefined,
      city: activity.city_tour_type || undefined,
      currency: estimate?.currency || activity.ticketPrices?.currency || 'INR',
      is_active: true,
      inclusionType: 'mandatory',
      ...(activity.ticketPrices?.adultPrice !== undefined
        ? { adult_price_paise: Math.round(Math.max(0, Number(activity.ticketPrices.adultPrice)) * 100) }
        : {}),
      ...(activity.ticketPrices?.childPrice !== undefined
        ? { child_price_paise: Math.round(Math.max(0, Number(activity.ticketPrices.childPrice)) * 100) }
        : {}),
      ticket_total: ticketTotal,
      cab_total: cabTotal,
      quoted_total: quotedTotal,
      selected_cab_type: hasLiveEstimate ? estimate?.selectedCabType || null : null,
      pricing_mode: hasLiveEstimate ? estimate?.mode : 'TICKET_ONLY',
    };
  };

  const searchQuotationActivities = async (city: string, name: string) => {
    setActivityCatalogError('');
    setIsLoadingActivityCatalog(true);
    try {
      const response = await fetchActivities({
        limit: 30,
        travelDate: coreState.tripInfo.startDate || derivedState.defaultHotelStartDate,
        ...(city.trim() ? { city: city.split(',', 1)[0].trim() } : {}),
        ...(name.trim() ? { name: name.trim() } : {}),
      });
      setActivityCatalog(response.items);
      try {
        const estimates = await estimateActivities({
          activityRefs: response.items.map((activity) => ({ activityRef: activity.id, ...(selectedCabLabel ? { cabType: selectedCabLabel } : {}) })),
          travelDate: coreState.tripInfo.startDate || derivedState.defaultHotelStartDate,
          ...quotationTravellers,
        });
        setActivityCatalogPrices(estimates.items);
      } catch {
        setActivityCatalogPrices({});
      }
    } catch (error) {
      setActivityCatalog([]);
      setActivityCatalogPrices({});
      setActivityCatalogError(error instanceof Error ? error.message : 'Unable to load activities.');
    } finally {
      setIsLoadingActivityCatalog(false);
    }
  };

  const openActivityPicker = async (dayIndex: number) => {
    const city = coreState.itinerary[dayIndex]?.place?.label || coreState.tripInfo.places[Math.min(dayIndex, coreState.tripInfo.places.length - 1)]?.label || '';
    setActivityPickerDayIndex(dayIndex);
    setActivitySearch('');
    setActivityCatalogCity(city);
    setActivityCatalog([]);
    setActivityCatalogPrices({});
    await searchQuotationActivities(city, '');
  };

  const formatActivityCatalogQuote = (activity: ActivitySummary, estimate?: ActivityEstimate) => {
    if (!estimate) return 'Price on request';
    if (estimate.status === 'AVAILABLE') {
      return formatMoney(Number(estimate.totalAmount ?? (Number(estimate.ticketTotal || 0) + Number(estimate.transferTotal || 0))), pricingState.currency);
    }
    const ticketOnlyTotal = getTicketOnlyTotal(activity, estimate, quotationTravellers);
    if (ticketOnlyTotal !== null) return `${formatMoney(ticketOnlyTotal, pricingState.currency)} tickets • transfer unavailable`;
    return estimate.error || 'Price on request';
  };

  const addQuotationActivity = async (catalogActivity: ActivitySummary) => {
    if (activityPickerDayIndex === null) return;
    setActivityCatalogError('');
    try {
      const estimate = await quoteActivity(catalogActivity.id, selectedCabLabel || undefined);
      const ticketOnlyTotal = getTicketOnlyTotal(catalogActivity, estimate, quotationTravellers);
      if (!estimate || (estimate.status !== 'AVAILABLE' && ticketOnlyTotal === null)) {
        throw new Error(estimate?.error || 'This activity is not currently available for the selected travellers.');
      }
      const activity = toQuotedActivity(catalogActivity, estimate);
      actions.setItinerary((current) => current.map((day, index) => index === activityPickerDayIndex
        ? { ...day, activities: [...(day.activities || []), activity] }
        : day));
      setActivityPickerDayIndex(null);
    } catch (error) {
      setActivityCatalogError(error instanceof Error ? error.message : 'Unable to price this activity.');
    }
  };

  useEffect(() => {
    if (!activitiesToPrice.length) return;

    let active = true;
    void estimateActivities({
      activityRefs: activitiesToPrice.map(({ activityRef }) => ({ activityRef: String(activityRef), ...(selectedCabLabel ? { cabType: selectedCabLabel } : {}) })),
      travelDate: coreState.tripInfo.startDate || derivedState.defaultHotelStartDate,
      ...quotationTravellers,
    }).then((result) => {
      if (!active) return;
      actions.setItinerary((current) => current.map((day) => ({
        ...day,
        activities: (day.activities || []).map((activity) => {
          const activityRef = activity.activity_ref || activity.activityRef;
          const estimate = activityRef ? result.items[String(activityRef)] : undefined;
          return estimate?.status === 'AVAILABLE'
            ? { ...activity, ticket_total: Number(estimate.ticketTotal || 0), cab_total: Number(estimate.transferTotal || 0), quoted_total: Number(estimate.totalAmount || 0), selected_cab_type: estimate.selectedCabType || null, pricing_mode: estimate.mode }
            : activity;
        }),
      })));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [actions, activitiesToPrice, coreState.tripInfo.startDate, derivedState.defaultHotelStartDate, quotationTravellers, selectedCabLabel]);
  const formatFlightMoment = (value: string) => {
    if (!value) return '';
    return latestBookingPlanSummary ? formatDisplayDateTime(value) : formatTime(value);
  };

  const packagePayload = buildPackagePayload();
  const providerRef: Record<string, unknown> = useMemo(() => ({
    ...packagePayload,
    packageCabPricing: builderState.quotationPackageState.transferRows,
    packageId: builderState.quotationPackageState.packageId || undefined,
    client: coreState.client,
    source: coreState.quotationSource || undefined,
    transferCalculation: builderState.derivedState.quotationTransferCalculation || undefined,
    transferOverride: pricingState.quotationTransferOverride,
    quotation: {
      origin: 'template',
      templateId: builderState.quotationPackageState.packageId || undefined,
      finalOverride: pricingState.quotationFinalOverride,
    },
  }), [
    packagePayload,
    builderState.quotationPackageState.packageId,
    builderState.quotationPackageState.transferRows,
    builderState.derivedState.quotationTransferCalculation,
    coreState.client,
    coreState.quotationSource,
    pricingState.quotationFinalOverride,
    pricingState.quotationTransferOverride,
  ]);

  const pdfTripInfo = useMemo(() => ({
    ...coreState.tripInfo,
    travellers: roomTravellers,
    totalPrice: quotationTotalPrice,
    hotelCategory: contentState.hotelCategory,
    transferType: contentState.transferType,
    inclusions: Array.from(new Set([
      ...(coreState.tripInfo.inclusions || []).map((value) => {
        const key = String(value || '').trim().toLowerCase();
        if (key === 'selected meals' || key === 'selected meal' || key === 'meals') return 'meal';
        if (key === 'sightseeing & activities' || key === 'sightseeing and activities' || key === 'activity') return 'activities';
        if (key === 'transfer' || key === 'transfers') return 'transfers';
        return key;
      }),
      ...((contentState.transferType || (builderState.derivedState.quotationTransferCalculation?.total || 0) > 0) ? ['transfers'] : []),
      ...(contentState.highlights.length > 0 ? ['activities'] : []),
      ...(contentState.visaDetails.length > 0 ? ['visa'] : []),
      ...(contentState.insuranceDetails.length > 0 ? ['insurance'] : []),
    ])),
  }), [
    builderState.derivedState.quotationTransferCalculation,
    contentState.highlights.length,
    contentState.hotelCategory,
    contentState.insuranceDetails.length,
    contentState.transferType,
    contentState.visaDetails.length,
    coreState.tripInfo,
    quotationTotalPrice,
    roomTravellers,
  ]);

  const handleHotelRoomsChange = (nextRooms: QuotationRoomOccupancy[]) => {
    const normalizedRooms = normalizeQuotationRooms(nextRooms);
    setHotelRooms(normalizedRooms);
    actions.handleTripInfoChange({
      name: 'travellers',
      value: toTravellerInfoFromRooms(normalizedRooms),
    });
  };

  useEffect(() => {
    const travellers = toTravellerInfoFromRooms(hotelRooms);
    const current = coreState.tripInfo.travellers;
    if (
      current.adults === travellers.adults
      && current.children === travellers.children
      && current.infants === travellers.infants
    ) return;
    actions.handleTripInfoChange({ name: 'travellers', value: travellers });
  }, [actions, coreState.tripInfo.travellers, hotelRooms]);

  const updateState = (next: Partial<PackageBuilderLocationState>) => {
    navigate('/quotation/builder', {
      replace: true,
      state: {
        ...locationState,
        ...(lastSharedQuotationAt ? { lastSharedQuotationAt } : {}),
        ...next,
      },
    });
  };

  const handleGenerateFromText = async () => {
    const trimmedText = rawItineraryText.trim();
    if (!trimmedText) {
      setItineraryExtractionError('Paste itinerary text before generating.');
      return;
    }

    setIsExtractingItinerary(true);
    setItineraryExtractionError('');
    try {
      const response = await extractQuotationItinerary({
        rawText: trimmedText,
        queryId: sourceQueryId ?? null,
        customerHint: {
          name: coreState.client.name || null,
          phoneNumber: coreState.client.phoneNumber || null,
          email: coreState.client.email || null,
        },
        tripStartDateHint: coreState.tripInfo.startDate || null,
      });

      const seed = response.builderSeed;
      actions.setClient(seed.client);
      actions.handleTripInfoChange({ name: 'title', value: seed.tripInfo.title });
      actions.handleTripInfoChange({ name: 'subtitle', value: seed.tripInfo.subtitle || '' });
      actions.handleTripInfoChange({ name: 'tripOverview', value: seed.tripInfo.tripOverview || '' });
      actions.handleTripInfoChange({ name: 'startDate', value: seed.tripInfo.startDate || '' });
      actions.handleTripInfoChange({ name: 'places', value: seed.tripInfo.places });
      actions.handleTripInfoChange({ name: 'travellers', value: seed.tripInfo.travellers });
      actions.handleTripInfoChange({ name: 'inclusions', value: seed.tripInfo.inclusions || [] });
      actions.handleTripInfoChange({ name: 'totalPrice', value: Number(seed.tripInfo.totalPrice || 0) });
      actions.setQuotationSource('text_itinerary_ai');
      actions.setQuotationPackageId(null);
      actions.setQuotationTransferRows([]);
      actions.setHighlights([]);
      actions.setExclusions(seed.exclusions || []);
      actions.setVisaDetails((seed.visaDetails || []).map((detail) => ({
        type: detail.type,
        price: Number(detail.price || 0),
        validity: detail.validity || '',
        documents: detail.documents || [],
        timeRequired: detail.timeRequired || '',
        currency: detail.currency === 'USD' ? 'USD' : 'INR',
      })));
      actions.setInsuranceDetails((seed.insuranceDetails || []).map((detail) => ({
        name: detail.name,
        price: Number(detail.price || 0),
        currency: detail.currency || 'INR',
        description: detail.description || '',
      })));
      actions.setMealPlan(seed.mealPlan || 'None');
      actions.setTransferType(seed.tripInfo.transferType || '');
      actions.setHotelCategory(seed.tripInfo.hotelCategory || '');
      setHotelRooms(normalizeQuotationRooms([
        {
          adults: Math.max(1, Number(seed.tripInfo.travellers.adults || 1)),
          children: Math.max(0, Number(seed.tripInfo.travellers.children || 0)),
          childrenAges: [],
        },
      ]));
      setItineraryExtractionWarnings(response.warnings || []);
      setPendingBuilderSeed(seed);
      updateState({
        quotationStep: 'builder',
        packageToCopy: undefined,
      });
    } catch (error) {
      setItineraryExtractionError(error instanceof Error ? error.message : 'Failed to generate quotation from text.');
    } finally {
      setIsExtractingItinerary(false);
    }
  };

  const loadAirportOptions = async (inputValue: string): Promise<LocationOption[]> => {
    try {
      const result = await loadAirportLocations(inputValue);
      const options = result || [];
      if (inputValue.trim() && options.length === 0) {
        return [{ label: inputValue.trim(), value: inputValue.trim(), type: 'airport' }];
      }
      return options;
    } catch {
      if (inputValue.trim()) {
        return [{ label: inputValue.trim(), value: inputValue.trim(), type: 'airport' }];
      }
      return [];
    }
  };

  const renderFlightSummary = (flight: Flight, index: number, onRemove: (dayIndex: number, flightIndex: number) => void) => {
    const dayIndex = coreState.itinerary.findIndex((day) => (day.flights || []).includes(flight));
    const flightIndex = dayIndex >= 0 ? (coreState.itinerary[dayIndex].flights || []).indexOf(flight) : -1;

    return (
      <div key={`${flight.flightNumber}-${index}`} className="quotation-choice-row">
        <div className="quotation-choice-row__main">
          <div className="quotation-choice-row__logo">
            {flight.provider ? (
              <img src={`https://cdn.otaprime.com/airline/${flight.provider}.gif`} alt={flight.airline} />
            ) : (
              <span>{flight.airline.slice(0, 2).toUpperCase()}</span>
            )}
          </div>
          <div>
            <h4>{flight.airline}</h4>
            <p>{flight.from} to {flight.to}</p>
          </div>
        </div>
        <div className="quotation-choice-row__meta">
          <strong>{formatFlightMoment(flight.departureTime)} - {formatFlightMoment(flight.arrivalTime)}</strong>
          <span>{flight.duration} • {flight.stops > 0 ? `${flight.stops} stop` : 'Non stop'}</span>
        </div>
        <div className="quotation-stay-row__actions">
          <div className="quotation-choice-row__price">{formatMoney(flight.price, pricingState.currency)}</div>
          {dayIndex >= 0 && flightIndex >= 0 && (
            <button
              type="button"
              className="quotation-action-btn"
              onClick={() => onRemove(dayIndex, flightIndex)}
            >
              Remove
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderHotelSummary = (hotel: Hotel & { dayIndex?: number }, index: number, onChange: (dayIndex: number) => void) => (
    <div key={`${hotel.hotelId}-${index}`} className="quotation-stay-row">
      <img src={hotel.thumbnail || 'https://cdn.otaprime.com/logo-new.png'} alt={hotel.name} />
      <div className="quotation-stay-row__body">
        <div>
          <h4>{hotel.name}</h4>
          <p>
            {hotel.starRating || '—'} Star
            {' • '}
            {hotel.roomType || 'Selected room'}
            {hotel.hotelRef || hotel.hotelCode
              ? ` • ${hotel.hotelRef || hotel.hotelCode}`
              : ''}
          </p>
          {hotel.address && (
            <p>{hotel.address}</p>
          )}
        </div>
        <div className="quotation-stay-row__actions">
          <strong>{formatMoney(hotel.display_price || hotel.price, pricingState.currency)}</strong>
          <button type="button" className="quotation-action-btn" onClick={() => onChange(resolvePlaceIndexForDay(hotel.dayIndex || 0))}>Change Hotel</button>
        </div>
      </div>
    </div>
  );

  const renderActivitySummary = (
    entry: { activity: Activity; activityIndex: number; dayIndex: number; day: number },
    onRemove: (dayIndex: number, activityIndex: number) => void
  ) => {
    const cabTotal = Number(entry.activity.cab_total || 0);
    const quotedTotal = Number(entry.activity.quoted_total);
    const hasQuotedPrice = Number.isFinite(quotedTotal) && quotedTotal >= 0;
    const fallbackPricePaise = Number(entry.activity.from_price_paise || 0);
    const fallbackAdultPrice = entry.activity.adult_price_paise === undefined
      ? (fallbackPricePaise ? Math.round(fallbackPricePaise / 100) : Number((entry.activity as any).price || 0))
      : Math.round(Number(entry.activity.adult_price_paise || 0) / 100);
    const fallbackChildPrice = entry.activity.child_price_paise === undefined
      ? fallbackAdultPrice
      : Math.round(Number(entry.activity.child_price_paise || 0) / 100);
    const fallbackInfantPrice = Math.round(Number(entry.activity.infant_price_paise || 0) / 100);
    const fallbackTicketTotal = fallbackAdultPrice * quotationTravellers.adults
      + fallbackChildPrice * quotationTravellers.children
      + fallbackInfantPrice * quotationTravellers.infants;
    const fallbackTotal = fallbackTicketTotal + cabTotal;
    const description = cleanActivityDescription(entry.activity.description);

    return (
      <div key={`${entry.activity.id || entry.activity.slug}-${entry.dayIndex}-${entry.activityIndex}`} className="quotation-choice-row quotation-activity-row">
        <div className="quotation-choice-row__main">
          {entry.activity.picture_url ? (
            <img className="quotation-activity-row__image" src={entry.activity.picture_url} alt={entry.activity.name} />
          ) : (
            <div className="quotation-choice-row__logo quotation-activity-row__image quotation-activity-row__image--fallback">
            <span>{entry.day}</span>
            </div>
          )}
          <div className="quotation-activity-row__details">
            <h4>{entry.activity.name}</h4>
            <p>Day {entry.day}{entry.activity.city ? ` • ${entry.activity.city}` : ''}{entry.activity.selected_cab_type ? ` • ${entry.activity.selected_cab_type} cab` : ''}</p>
            {description && <p className="quotation-activity-row__description">{description}</p>}
          </div>
        </div>
        <div className="quotation-stay-row__actions">
          <div className="quotation-choice-row__price">
            <strong>{formatMoney(hasQuotedPrice ? quotedTotal : fallbackTotal, pricingState.currency)}</strong>
          </div>
          <button
            type="button"
            className="quotation-action-btn"
            onClick={() => onRemove(entry.dayIndex, entry.activityIndex)}
          >
            Remove
          </button>
        </div>
      </div>
    );
  };

  const handleCabSelection = (rowId?: string, tempId?: string) => {
    const selectedRowKey = rowId || tempId;
    if (!selectedRowKey) return;
    actions.setQuotationTransferOverride(null);
    actions.setQuotationTransferRows((prev) => prev.map((row) => {
      const currentKey = row.id || row.tempId;
      return {
        ...row,
        isActive: currentKey === selectedRowKey,
      };
    }));
  };

  return (
    <Layout>
      {exitConfirmation}
      <div className="quotation-page">
        <div className="quotation-mode-tabs" role="tablist" aria-label="Quotation type">
          {([
            ['package', 'Package'],
            ['flight', 'Flights'],
            ['hotel', 'Hotels'],
          ] as const).map(([kind, label]) => (
            <button
              key={kind}
              type="button"
              role="tab"
              aria-selected={quotationKind === kind}
              className={quotationKind === kind ? 'active' : ''}
              onClick={() => {
                if (kind !== 'package') setHasOpenedStandaloneQuotation(true);
                updateState({ quotationKind: kind, quotationStep: 'builder' });
              }}
            >
              {label}
            </button>
          ))}
        </div>
      {quotationKind === 'package' && <ModalManager
        nightsCount={coreState.nightsCount}
        tripInfo={coreState.tripInfo}
        itinerary={coreState.itinerary}
        setItinerary={actions.setItinerary}
        startDate={coreState.tripInfo.startDate}
        travellers={coreState.tripInfo.travellers}
        hotelCache={assetsState.hotelCache}
        hotelMealTypeOverride={hotelMealType || undefined}
        hotelRooms={hotelRooms}
        flightReselectTrigger={builderState.quotationRepriceState.flightReselectTrigger}
        onFlightReselected={builderState.quotationRepriceState.markFlightsReselected}
        arrivalCityLabel={arrivalCityLabel}
        departureCityLabel={departureCityLabel}
      >
        {({
          openFlightModal,
          openHotelModal,
          openTransferModal,
          removeFlight,
          removeActivity,
        }: ModalCallbacks) => (
          <div className="quotation-workflow">
            <div className="quotation-workflow__hero">
              <div>
                <span className="quotation-workflow__eyebrow">Quotation Workspace</span>
                <h1>Quotation Builder</h1>
              </div>
            </div>

            <div className="quotation-workflow__grid quotation-workflow__grid--top">
              <WorkflowCard step={1} title="Select Base Package" subtitle="Choose a base package to build the quotation">
                {isVendorItineraryOpen && (
                  <div className="quotation-field quotation-field--full quotation-vendor-itinerary">
                    <label>Paste Vendor Itinerary</label>
                    <textarea
                      value={rawItineraryText}
                      onChange={(e) => setRawItineraryText(e.target.value)}
                      rows={10}
                      placeholder="Paste raw itinerary text from vendor email, WhatsApp, or PDF here..."
                    />
                    <div className="quotation-section-actions" style={{ marginTop: 12 }}>
                      <button
                        type="button"
                        className="quotation-primary-btn"
                        onClick={handleGenerateFromText}
                        disabled={isExtractingItinerary}
                      >
                        {isExtractingItinerary ? 'Generating...' : 'Generate From Text'}
                      </button>
                    </div>
                    {itineraryExtractionError && (
                      <div className="quotation-warning" style={{ marginTop: 12 }}>{itineraryExtractionError}</div>
                    )}
                    {itineraryExtractionWarnings.length > 0 && (
                      <div className="quotation-warning quotation-warning--soft" style={{ marginTop: 12 }}>
                        {itineraryExtractionWarnings[0].message}
                      </div>
                    )}
                  </div>
                )}
                <div className="quotation-package-search">
                  <input
                    type="text"
                    value={packageSearch}
                    onChange={(e) => {
                      setPackageSearch(e.target.value);
                      setPackagePage(1);
                    }}
                    placeholder="Search packages..."
                  />
                </div>
                <div className="quotation-package-list">
                  {loadingPackages && <div className="quotation-empty-state">Loading packages...</div>}
                  {!loadingPackages && visiblePackages.map((pkg) => {
                    const active = selectedPackage?.id === pkg.id;
                    return (
                      <button
                        key={pkg.id || pkg.slug}
                        type="button"
                        className={`quotation-package-card${active ? ' active' : ''}`}
                        onClick={() => updateState({ packageToCopy: pkg, quotationStep: 'builder' })}
                      >
                        <img src={getPackageImage(pkg)} alt={pkg.title} />
                        <div className="quotation-package-card__body">
                          <div className="quotation-package-card__top">
                            <div>
                              <h3>{pkg.title}</h3>
                              <p>{formatDuration(pkg)}</p>
                            </div>
                            <strong>{formatMoney(Number(pkg.publicStartingPrice ?? pkg.public_starting_price ?? 0))}</strong>
                          </div>
                          <div className="quotation-package-card__tags">
                            {(pkg.stops || []).map((stop, index) => (
                              <span key={`${stop.destinationSlug ?? stop.destination_slug ?? stop.cityName ?? stop.city_name ?? index}-${index}`}>
                                {stop.cityName ?? stop.city_name ?? 'Unnamed stop'} · {stop.nights}N
                              </span>
                            ))}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                  {!loadingPackages && packageSearch.trim() && visiblePackages.length === 0 && (
                    <div className="quotation-empty-state">No packages found.</div>
                  )}
                </div>
                <button
                  type="button"
                  className="quotation-vendor-itinerary-toggle"
                  onClick={() => setIsVendorItineraryOpen((isOpen) => !isOpen)}
                  aria-expanded={isVendorItineraryOpen}
                >
                  {isVendorItineraryOpen ? 'Hide vendor itinerary input' : 'Paste vendor itinerary instead'}
                </button>
              </WorkflowCard>

              <WorkflowCard step={2} title="Trip Details" subtitle="Enter trip details to proceed">
                <div className="quotation-form-grid">
                  <div className="quotation-field quotation-field--full">
                    <label>Base Package</label>
                    <div className="quotation-readonly">{selectedPackage?.title || 'Select a package from the left'}</div>
                  </div>
                  <div className="quotation-field">
                    <label>Start Date</label>
                    <input
                      type="date"
                      value={coreState.tripInfo.startDate}
                      onChange={(e) => actions.handleTripInfoChange(e)}
                      name="startDate"
                    />
                  </div>
                  <div className="quotation-field">
                    <label>End Date (Derived)</label>
                    <input type="date" value={derivedEndDate} disabled readOnly />
                  </div>
                  <div className="quotation-duration">
                    Duration: {coreState.nightsCount} Nights / {coreState.nightsCount + 1} Days
                  </div>
                  <div className="quotation-field quotation-field--full">
                    <label>Rooms & Travellers</label>
                    <RoomsTravellersDropdown value={hotelRooms} onChange={handleHotelRoomsChange} />
                  </div>
                  <div className="quotation-field quotation-field--full">
                    <label>Starting From</label>
                    <AsyncSelect<LocationOption, false>
                      cacheOptions
                      defaultOptions
                      loadOptions={loadAirportOptions}
                      getOptionLabel={formatAirportOptionLabel}
                      getOptionValue={(o) => o.value}
                      onChange={(opt) => actions.handleTripInfoChange({ name: 'from', value: opt ? { label: opt.label, value: opt.value } : undefined })}
                      value={coreState.tripInfo.from ? { label: coreState.tripInfo.from.label, value: coreState.tripInfo.from.value, type: 'airport' } : null}
                      placeholder="Type a city or airport"
                      isClearable
                      menuPortalTarget={document.body}
                      styles={{ menuPortal: (base) => ({ ...base, zIndex: 9999 }) }}
                    />
                  </div>
                </div>
              </WorkflowCard>

              <WorkflowCard step={3} title="Select Flights" subtitle="Choose your preferred flights">
                {builderState.quotationRepriceState.flightReselectRequired && (
                  <div className="quotation-warning">Travel dates or passengers changed. Please reselect flights.</div>
                )}
                {isOlderThanFourHours(lastSharedQuotationAt) && (
                  <div className="quotation-warning quotation-warning--soft">Outdated please reselect the flight</div>
                )}
                <div className="quotation-section-actions">
                  {hasDistinctArrivalAndDepartureCities ? (
                    <>
                      <button type="button" className="quotation-primary-btn" onClick={() => openFlightModal('arrival')}>
                        {arrivalFlightCount ? 'Change Arrival Flight' : 'Select Arrival Flight'}
                      </button>
                      <button type="button" className="quotation-primary-btn" onClick={() => openFlightModal('departure')}>
                        {departureFlightCount ? 'Change Departure Flight' : 'Select Departure Flight'}
                      </button>
                    </>
                  ) : (
                    <button type="button" className="quotation-primary-btn" onClick={() => openFlightModal('roundTrip')}>
                      {flightList.length ? 'Change Round Trip Flight' : 'Select Round Trip Flight'}
                    </button>
                  )}
                </div>
                <div className="quotation-choice-list">
                  {flightList.length > 0 ? flightList.map((flight, index) => renderFlightSummary(flight, index, removeFlight)) : (
                    <div className="quotation-empty-state">No flights selected yet.</div>
                  )}
                </div>
              </WorkflowCard>
            </div>

            {activityPickerDayIndex !== null && (
              <div className="modal-backdrop" onClick={() => setActivityPickerDayIndex(null)}>
                <div className="modal rayna-activity-picker" role="dialog" aria-modal="true" aria-label="Pick quotation activity" onClick={(event) => event.stopPropagation()}>
                  <button className="close-btn" type="button" aria-label="Close" onClick={() => setActivityPickerDayIndex(null)}>×</button>
                  <h2>Pick Activity for Day {coreState.itinerary[activityPickerDayIndex]?.day || activityPickerDayIndex + 1}</h2>
                  <div className="package-editor-tag-input-container">
                    <input
                      className="package-editor-input"
                      value={activityCatalogCity}
                      onChange={(event) => setActivityCatalogCity(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void searchQuotationActivities(activityCatalogCity, activitySearch);
                        }
                      }}
                      placeholder="City name"
                      aria-label="Activity city"
                    />
                    <input
                      className="package-editor-input"
                      value={activitySearch}
                      onChange={(event) => setActivitySearch(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void searchQuotationActivities(activityCatalogCity, activitySearch);
                        }
                      }}
                      placeholder="Activity name"
                      aria-label="Activity name"
                    />
                    <button type="button" className="package-editor-btn-primary" onClick={() => void searchQuotationActivities(activityCatalogCity, activitySearch)}>Search</button>
                  </div>
                  {activityCatalogError && <div className="quotation-warning">{activityCatalogError}</div>}
                  {isLoadingActivityCatalog ? <p>Loading activities…</p> : (
                    <div className="package-editor-addon-list">
                      {activityCatalog.map((activity) => {
                        const provider = activity.provider || 'RAYNA';
                        return (
                          <button
                            type="button"
                            className="package-editor-addon-card"
                            key={activity.id}
                            onClick={() => void addQuotationActivity(activity)}
                            style={{ display: 'grid', gridTemplateColumns: activity.image_path ? '104px minmax(0, 1fr) auto' : 'minmax(0, 1fr) auto', gap: 14, alignItems: 'start', textAlign: 'left' }}
                          >
                            {activity.image_path && <img src={activity.image_path} alt="" style={{ width: 104, height: 82, borderRadius: 8, objectFit: 'cover' }} />}
                            <span>
                              <strong>{activity.name}</strong>
                              <span className="guided-package-builder__activity-source">{provider}</span>
                              <span style={{ display: 'block', marginTop: 6, lineHeight: 1.4 }}>{cleanActivityDescription(activity.description).slice(0, 180) || 'Activity details available on selection.'}</span>
                            </span>
                            <strong style={{ whiteSpace: 'nowrap' }}>{formatActivityCatalogQuote(activity, activityCatalogPrices[activity.id])}</strong>
                          </button>
                        );
                      })}
                      {activityCatalog.length === 0 && <p>No activities found. Try another city or activity name.</p>}
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="quotation-workflow__grid quotation-workflow__grid--bottom">
              <WorkflowCard step={4} title="Customize the Trip" subtitle="Modify hotel and cab as per customer's preference">
                <div className="quotation-customise-block">
                  <div className="quotation-customise-section">
                    <div className="quotation-customise-section__head">
                      <h3>Hotel</h3>
                    </div>
                    {hotelList.length > 0 ? hotelList.map((hotel, index) => renderHotelSummary(hotel, index, openHotelModal)) : (
                      <div className="quotation-empty-state">No hotels selected yet.</div>
                    )}
                  </div>

                  <div className="quotation-customise-section">
                    <div className="quotation-customise-section__head">
                      <h3>Mandatory activities</h3>
                    </div>
                    {coreState.itinerary.map((day, dayIndex) => {
                      const dayActivities = activityList.filter((entry) => entry.dayIndex === dayIndex);
                      return (
                        <div key={`quotation-activities-day-${dayIndex}`} className="quotation-choice-list">
                          <div className="quotation-customise-section__head">
                            <h4>Day {day.day || dayIndex + 1}</h4>
                            <button type="button" className="quotation-action-btn" onClick={() => void openActivityPicker(dayIndex)}>+ Add activity</button>
                          </div>
                          {dayActivities.length > 0 ? dayActivities.map((entry) => renderActivitySummary(entry, removeActivity)) : (
                            <div className="quotation-empty-state">No mandatory activities for this day.</div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <div className="quotation-customise-section">
                    <div className="quotation-customise-section__head"><h3>Visa & Insurance</h3></div>
                    {contentState.visaDetails.map((visa, index) => (
                      <div key={`visa-${index}`} className="quotation-choice-row"><div className="quotation-choice-row__main"><div><h4>{visa.type || 'Visa'}</h4><p>Visa</p></div></div><div className="quotation-choice-row__price">{formatMoney(Number(visa.price || 0), pricingState.currency)} / person</div></div>
                    ))}
                    {contentState.insuranceDetails.map((insurance, index) => (
                      <div key={`insurance-${index}`} className="quotation-choice-row"><div className="quotation-choice-row__main"><div><h4>{insurance.name || 'Insurance'}</h4><p>Insurance</p></div></div><div className="quotation-choice-row__price">{formatMoney(Number(insurance.price || 0), pricingState.currency)} / person</div></div>
                    ))}
                    {!contentState.visaDetails.length && !contentState.insuranceDetails.length && <div className="quotation-empty-state">No visa or insurance selected.</div>}
                  </div>

                  <div className="quotation-customise-section">
                  <div className="quotation-customise-section__head">
                      <h3>Cab / Transfer</h3>
                    </div>
                    {displayedCabOptions.length > 0 ? (
                      <div className="quotation-choice-list">
                        {displayedCabOptions.map((row) => {
                          const rowKey = row.id || row.tempId || row.cabType;
                          const occupancy = Number(row.maxOccupancy || 0);
                          const disabled = occupancy > 0 && occupancy < Math.max(1, totalPassengers);
                          const isSelected = selectedCabRowId
                            ? selectedCabRowId === (row.id || row.tempId)
                            : formatCabType(row.cabType) === formatCabType(selectedCabLabel || '');

                          return (
                            <label
                              key={rowKey}
                              className={`quotation-choice-row quotation-choice-row--selectable${isSelected ? ' active' : ''}${disabled ? ' disabled' : ''}`}
                            >
                              <div className="quotation-choice-row__main">
                                <div className="quotation-choice-row__logo">
                                  <span>{occupancy || '-'}</span>
                                </div>
                                <div>
                                  <h4>{row.cabType}</h4>
                                  <p>Up to {occupancy} passengers</p>
                                </div>
                              </div>
                              <div className="quotation-choice-row__meta">
                                <strong>{formatMoney(Number(row.price || 0), pricingState.currency)}</strong>
                                <span>{disabled ? `Needs ${totalPassengers} pax support` : 'Package cab pricing'}</span>
                              </div>
                              <input
                                type="radio"
                                name="quotation-cab-selection"
                                checked={isSelected}
                                disabled={disabled}
                                onChange={() => handleCabSelection(row.id, row.tempId)}
                              />
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="quotation-empty-state">No cab pricing available for this package yet.</div>
                    )}
                  </div>
                </div>
              </WorkflowCard>

              <WorkflowCard step={5} title="Review & Confirm" subtitle="Review your itinerary and proceed">
                <div className="quotation-review-list">
                  <div className="quotation-review-row"><span>Base Package</span><strong>{selectedPackage?.title || '-'}</strong></div>
                  <div className="quotation-review-row"><span>Travel Dates</span><strong>{coreState.tripInfo.startDate || '-'} {derivedEndDate ? `- ${derivedEndDate}` : ''}</strong></div>
                  <div className="quotation-review-row"><span>Rooms & Travellers</span><strong>{hotelRoomSummary}</strong></div>
                  <div className="quotation-review-row"><span>Starting From</span><strong>{coreState.tripInfo.from?.label || '-'}</strong></div>
                  {itineraryExtractionWarnings.length > 0 && (
                    <div className="quotation-review-row"><span>Extraction Warnings</span><strong>{itineraryExtractionWarnings.length}</strong></div>
                  )}
                </div>

                <div className="quotation-field quotation-field--full" style={{ marginTop: 16 }}>
                  <label>Markup (%)</label>
                  <input
                    type="number"
                    min={MIN_QUOTATION_MARKUP_PERCENT}
                    step="1"
                    value={pricingState.markupType === 'percent' ? pricingState.markupValue : DEFAULT_QUOTATION_MARKUP_PERCENT}
                    onChange={(e) => {
                      const raw = e.target.value;
                      const nextValue = raw === ''
                        ? DEFAULT_QUOTATION_MARKUP_PERCENT
                        : Math.max(MIN_QUOTATION_MARKUP_PERCENT, Number(raw || 0));
                      actions.setMarkupType('percent');
                      actions.setMarkupValue(nextValue);
                    }}
                    placeholder={`${DEFAULT_QUOTATION_MARKUP_PERCENT}`}
                  />
                <div className="quotation-empty-state" style={{ marginTop: 8 }}>
                    Default markup is {DEFAULT_QUOTATION_MARKUP_PERCENT}%. Minimum allowed markup is {MIN_QUOTATION_MARKUP_PERCENT}%. GST at 5% is applied to the final amount after markup.
                  </div>
                </div>

                <div className="quotation-summary-panels">
                  <div className="quotation-summary-panel">
                    <span>Flights</span>
                    <strong>{formatMoney(derivedState.flightsTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Hotel</span>
                    <strong>{formatMoney(derivedState.hotelsTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Activities</span>
                    <strong>{formatMoney(derivedState.activitiesTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Cab / Transfer</span>
                    <strong>{formatMoney(derivedState.transfersTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Visa</span>
                    <strong>{formatMoney(derivedState.visaTotal, pricingState.currency)}</strong>
                  </div>
                  <div className="quotation-summary-panel">
                    <span>Insurance</span>
                    <strong>{formatMoney(derivedState.insuranceTotal, pricingState.currency)}</strong>
                  </div>
                </div>

                <div className="quotation-total-card">
                  <span>Total Package Price (incl. GST)</span>
                  <strong>{formatMoney(quotationTotalPrice, pricingState.currency)}</strong>
                </div>

                <div className="quotation-section-actions">
                  <button type="button" className="quotation-primary-btn" onClick={() => updateState({ quotationStep: 'review' })}>
                    Proceed to PDF Preview
                  </button>
                </div>
              </WorkflowCard>

              <WorkflowCard step={6} title="Quotation Preview (PDF)" subtitle="Download and share the final quotation">
                {step !== 'review' && (
                  <div className="quotation-empty-state">Proceed to PDF Preview to render the customer-facing quotation.</div>
                )}
                {step === 'review' && (
                  <>
                    <div className="quotation-preview-actions">
                      {role !== 'sales' && (
                        <button
                          type="button"
                          className="quotation-primary-btn"
                          onClick={() => setIsShareConfirmationOpen(true)}
                        >
                          Download &amp; Share
                        </button>
                      )}
                    </div>
                    {isShareConfirmationOpen && (
                      <div className="modal-backdrop" onClick={() => setIsShareConfirmationOpen(false)}>
                        <section
                          className="modal quotation-share-confirmation"
                          role="alertdialog"
                          aria-modal="true"
                          aria-labelledby="quotation-share-confirmation-title"
                          aria-describedby="quotation-share-confirmation-message"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <div className="modal-header">
                            <h2 id="quotation-share-confirmation-title">Share quotation?</h2>
                          </div>
                          <p id="quotation-share-confirmation-message">
                            The PDF will be downloaded and this quotation will be shared with the customer.
                          </p>
                          <div className="modal-actions">
                            <button
                              type="button"
                              className="secondary"
                              onClick={() => setIsShareConfirmationOpen(false)}
                            >
                              Cancel
                            </button>
                            <ShareQuotationButton
                              nightsCount={coreState.nightsCount}
                              queryId={sourceQueryId}
                              startDate={coreState.tripInfo.startDate}
                              hotelRooms={hotelRooms}
                              totalPrice={quotationTotalPrice}
                              providerRef={providerRef}
                              pdfRef={quotationPdfRef}
                              currency={pricingState.currency}
                              title={coreState.tripInfo.title}
                              client={coreState.client}
                              customerId={coreState.queryCustomerId}
                              assignedId={coreState.queryAssignedId}
                              createdBy={coreState.queryCreatedBy}
                              label="Download & Share"
                              download
                              shareDraft
                              className="primary"
                              onAfterShare={() => setIsShareConfirmationOpen(false)}
                            />
                          </div>
                        </section>
                      </div>
                    )}
                    {!sourceQueryId && (
                      <div className="quotation-warning quotation-warning--soft">
                        This quotation is not attached to a query yet. The PDF will download, but it cannot be shared until it is attached to a query.
                      </div>
                    )}
                    <div className="quotation-pdf-shell">
                      <div ref={quotationPdfRef}>
                        <PreviewPDF
                          client={coreState.client}
                          tripInfo={pdfTripInfo}
                          itinerary={displayItinerary}
                          quotationCreatedAt={coreState.quotationCreatedAt}
                          generalRules=""
                          highlights={(contentState.highlights || []).join(', ')}
                          highlightImages={[]}
                          inclusionImages={[]}
                          itineraryImages={[]}
                          addons={contentState.addons}
                          visaDetails={contentState.visaDetails}
                          insuranceDetails={contentState.insuranceDetails}
                          exclusions={contentState.exclusions}
                          mealPlan={contentState.mealPlan}
                          selectedHotelRoomCount={hotelRooms.length}
                          selectedCab={{
                            cabType: builderState.derivedState.quotationTransferCalculation?.cabType,
                            maxOccupancy: builderState.derivedState.quotationTransferCalculation?.maxOccupancy,
                            transferType: contentState.transferType,
                          }}
                          paymentCalendar={{
                            flightCost: derivedState.flightsTotal,
                            hotelCost: derivedState.hotelsTotal,
                            cabCost: derivedState.transfersTotal,
                            totalPackageCost: quotationTotalPrice,
                            currency: pricingState.currency,
                          }}
                        />
                      </div>
                    </div>
                  </>
                )}
              </WorkflowCard>
            </div>

            {modalState.showVisaModal && (
              <VisaDetailsModal
                currency={pricingState.currency}
                onClose={() => actions.setShowVisaModal(false)}
                onSave={(detail: VisaDetail) => {
                  actions.setVisaDetails((prev) => [...prev, detail]);
                  actions.setShowVisaModal(false);
                }}
              />
            )}

            {modalState.showInsuranceModal && (
              <InsuranceAddonModal
                currency={pricingState.currency}
                travelStartDate={coreState.tripInfo.startDate}
                nightsCount={coreState.nightsCount}
                noOfPax={derivedState.totalPax}
                onClose={() => actions.setShowInsuranceModal(false)}
                onSave={(addon: InsuranceDetail) => {
                  actions.setInsuranceDetails((prev) => [...prev, addon]);
                  actions.setShowInsuranceModal(false);
                }}
              />
            )}

            {modalState.showCustomVisaModal && (
              <VisaDetailsModal
                currency={pricingState.currency}
                onClose={() => actions.setShowCustomVisaModal(false)}
                onSave={(detail: VisaDetail) => {
                  actions.setCustomVisaDetails((prev) => [...prev, detail]);
                  actions.setShowCustomVisaModal(false);
                }}
              />
            )}

            {modalState.showCustomInsuranceModal && (
              <InsuranceAddonModal
                currency={pricingState.currency}
                travelStartDate={coreState.tripInfo.startDate}
                nightsCount={coreState.nightsCount}
                noOfPax={derivedState.totalPax}
                onClose={() => actions.setShowCustomInsuranceModal(false)}
                onSave={(addon: InsuranceDetail) => {
                  actions.setCustomInsuranceDetails((prev) => [...prev, addon]);
                  actions.setShowCustomInsuranceModal(false);
                }}
              />
            )}

            {modalState.showCustomFlightModal && (
              <CustomFlightModal
                onClose={() => actions.setShowCustomFlightModal(false)}
                onSave={(flight) => {
                  actions.setCustomFlights((prev) => [...prev, flight]);
                  actions.setShowCustomFlightModal(false);
                }}
              />
            )}

          </div>
        )}
      </ModalManager>}
      {hasOpenedStandaloneQuotation && <div hidden={quotationKind === 'package'}>
        <StandaloneQuotationWorkflow
          kind={quotationKind === 'hotel' ? 'hotel' : 'flight'}
          review={step === 'review'}
          sourceQueryId={sourceQueryId}
          client={coreState.client}
          customerId={coreState.queryCustomerId}
          assignedId={coreState.queryAssignedId}
          createdBy={coreState.queryCreatedBy}
          hotelRooms={hotelRooms}
          onHotelRoomsChange={handleHotelRoomsChange}
          defaultStartDate={coreState.tripInfo.startDate}
          defaultPlace={coreState.tripInfo.places[0]}
          role={role}
          onProceed={() => updateState({ quotationStep: 'review' })}
        />
      </div>}
      </div>
    </Layout>
  );
};

export default QuotationWorkflowPage;
